import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  Paper,
  TextField,
  Alert,
  Divider,
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

const CARD_BG = '#0b1522';
const SURFACE = '#0e1a2b';
const BORDER = 'rgba(120,140,180,.18)';
const SUB_BORDER = 'rgba(120,140,180,.14)';
const TEXT_MAIN = '#eaf2ff';
const TEXT_SUB = '#9fb4d8';

const Card = styled(Paper)({
  borderRadius: 18,
  padding: 16,
  border: `1px solid ${BORDER}`,
  background: CARD_BG,
  boxShadow: '0 18px 40px rgba(8,20,40,.45)',
});

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: SURFACE,
    color: TEXT_MAIN,
    '& fieldset': { borderColor: BORDER },
    '&:hover fieldset': { borderColor: '#7fb0ff' },
    '&.Mui-focused fieldset': {
      borderColor: '#7fb0ff',
      boxShadow: '0 0 0 3px rgba(127,176,255,.18)',
    },
    '& input': { color: TEXT_MAIN },
  },
  '& .MuiInputLabel-root': { color: TEXT_SUB },
  '& .MuiInputLabel-root.Mui-focused': { color: '#b8d8ff' },
  '& .MuiFormHelperText-root': { marginLeft: 0, color: TEXT_SUB },
};

const btnPrimaryDanger = {
  height: 44,
  borderRadius: 12,
  fontWeight: 900,
  textTransform: 'none' as const,
  color: '#fff',
  background: 'linear-gradient(90deg,#ef4444,#fb7185)',
  border: '1px solid rgba(251,113,133,.35)',
  boxShadow: '0 8px 20px rgba(239,68,68,.22)',
  '&:hover': {
    filter: 'brightness(1.05)',
    boxShadow: '0 10px 24px rgba(239,68,68,.28)',
  },
};

const btnOutline = {
  height: 44,
  borderRadius: 12,
  fontWeight: 800,
  textTransform: 'none' as const,
  color: TEXT_MAIN,
  border: `1px solid ${BORDER}`,
  background: 'transparent',
  '&:hover': { borderColor: '#7fb0ff', boxShadow: '0 6px 16px rgba(16,24,40,.25)' },
};

interface WithdrawConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  loading?: boolean;
  userEmail?: string;
  onConfirm: () => Promise<void> | void;
}

const WithdrawConfirmDialog: React.FC<WithdrawConfirmDialogProps> = ({
  open,
  onClose,
  loading = false,
  userEmail = '',
  onConfirm,
}) => {
  const [confirmText, setConfirmText] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const handleClose = () => {
    if (loading) return;
    setConfirmText('');
    setLocalError(null);
    onClose();
  };

  const handleConfirm = async () => {
    setLocalError(null);

    if (confirmText.trim() !== '탈퇴') {
      setLocalError('확인을 위해 "탈퇴"를 정확히 입력하세요.');
      return;
    }

    await onConfirm();
    setConfirmText('');
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ elevation: 0, sx: { borderRadius: 0, background: 'transparent' } }}
      BackdropProps={{ sx: { backgroundColor: 'rgba(0,0,0,.55)' } }}
    >
      <Card>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography sx={{ color: TEXT_MAIN, fontWeight: 900, fontSize: 18 }}>
            회원탈퇴 확인
          </Typography>
          <IconButton onClick={handleClose} sx={{ color: TEXT_SUB }}>
            <CloseRoundedIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ px: 0, pt: 0.5, pb: 0 }}>
          <Alert
            severity="warning"
            icon={<WarningAmberRoundedIcon />}
            sx={{ mb: 2, borderRadius: 2 }}
          >
            탈퇴를 진행하면 계정 접근이 중단되며, 일부 정보는 관련 법령 및 내부 정책에 따라 일정 기간 보관될 수 있습니다.
          </Alert>

          <Box
            sx={{
              p: 1.25,
              borderRadius: 2,
              border: `1px solid ${SUB_BORDER}`,
              background: alpha('#0e1a2b', 0.7),
              mb: 2,
            }}
          >
            <Typography sx={{ color: TEXT_MAIN, fontWeight: 900, fontSize: 13, mb: 0.75 }}>
              탈퇴 전 확인 사항
            </Typography>
            <Typography sx={{ color: TEXT_SUB, fontSize: 12, lineHeight: 1.7 }}>
              계정: {userEmail || '-'}
              <br />
              활성 구독 또는 결제 관련 상태가 있다면 먼저 해지 여부를 확인하는 것이 안전합니다.
              <br />
              탈퇴 후 복구가 제한될 수 있으므로 신중히 진행하세요.
            </Typography>
          </Box>

          <Divider sx={{ my: 2, borderColor: SUB_BORDER }} />

          <Typography sx={{ color: TEXT_MAIN, fontWeight: 900, fontSize: 13, mb: 0.75 }}>
            계속하려면 아래 입력칸에 <span style={{ color: '#fb7185' }}>탈퇴</span> 를 입력하세요.
          </Typography>

          <TextField
            fullWidth
            placeholder='탈퇴'
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            sx={fieldSx}
            disabled={loading}
          />

          {localError && (
            <Alert severity="error" sx={{ mt: 1.25, borderRadius: 2 }}>
              {localError}
            </Alert>
          )}

          <Divider sx={{ my: 2, borderColor: SUB_BORDER }} />

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleClose} disabled={loading} fullWidth sx={btnOutline}>
              취소
            </Button>
            <Button onClick={handleConfirm} disabled={loading} fullWidth sx={btnPrimaryDanger}>
              {loading ? '탈퇴 처리 중…' : '회원탈퇴 진행'}
            </Button>
          </Box>
        </DialogContent>
      </Card>
    </Dialog>
  );
};

export default WithdrawConfirmDialog;